import * as React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import type { ProjectDetailsProps } from '@/types';
import {
  User,
  Users,
  FileText,
  MessageSquare,
  Globe,
  Mail,
  Building,
  CheckCircle2,
  ExternalLink,
  Calendar,
  Clock,
  Sparkles,
} from 'lucide-react';
import { formatDate } from 'date-fns';
import { DATE_FORMAT } from '@/constants';
import { getDurationLabel } from '@/lib';
import { InternalProjectInfos } from './internal-project-infos';
import { useClientPermissions } from '@/hooks';

export function ProjectDetails({ project, isExternal }: ProjectDetailsProps) {
  const { isCompany } = useClientPermissions();

  const mentorInitials = React.useMemo(() => {
    if (!project.project_mentor) return '?';
    const parts = project.project_mentor.split('@')[0].split('.');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return project.project_mentor.slice(0, 2).toUpperCase();
  }, [project.project_mentor]);

  const school = Array.isArray(project.assigned_schools)
    ? project.assigned_schools[0]
    : project.assigned_schools;

  const stats = [
    {
      label: 'Enrollments',
      value: project.number_of_enrollments,
      icon: Users,
      color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
    },
    {
      label: 'Documents',
      value: project.number_of_docs,
      icon: FileText,
      color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
    },
    {
      label: 'Forums',
      value: project.number_of_forums,
      icon: MessageSquare,
      color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    },
  ];



  if (!project) {
    return (
      <div className="flex min-h-[400px] items-center justify-center rounded-2xl border border-dashed border-border/60 bg-muted/30">
        <div className="text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
            <FileText className="h-5 w-5 text-muted-foreground" />
          </div>
          <p className="text-muted-foreground">No project selected.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto space-y-8">
      {/* Stats Cards */}
      <div className="grid gap-4 sm:grid-cols-3">
        {stats.map((stat) => (
          <Card
            key={stat.label}
            className="p-0 group relative overflow-hidden border-border/40 bg-card/80 backdrop-blur-sm transition-all duration-300 hover:border-border hover:shadow-lg hover:shadow-black/5"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
            <CardContent className="relative flex items-center gap-4 p-5">
              <div
                className={`flex h-14 w-14 items-center justify-center rounded-2xl ${stat.color} transition-transform duration-300 group-hover:scale-110`}
              >
                <stat.icon className="h-6 w-6" />
              </div>
              <div>
                <p className="text-3xl font-bold tracking-tight">
                  {stat.value}
                </p>
                <p className="text-sm font-medium text-muted-foreground">
                  {stat.label}
                </p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Details Grid */}
      <div className="grid gap-8 lg:grid-cols-3">
        {/* Main Content */}
        <div className="space-y-6 lg:col-span-2">
          {/* Project Information */}
          <InternalProjectInfos project={project} />

          {/* Skills & SDGs */}
          {((project.expected_skills?.length ?? 0) > 0 ) && (
            <Card className="overflow-hidden border-border/40 bg-card/80 backdrop-blur-sm p-0">
              <div className="border-b border-border/40 bg-muted/30 px-6 py-4">
                <h2 className="flex items-center gap-3 text-lg font-semibold">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10">
                    <CheckCircle2 className="h-4.5 w-4.5 text-primary" />
                  </div>
                  Skills & Goals
                </h2>
              </div>
              <CardContent className="p-6">
                {(project.expected_skills?.length ?? 0) > 0 && (
                  <div className="mb-6">
                    <h3 className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      <Sparkles className="h-3.5 w-3.5" />
                      Expected Skills
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {project.expected_skills?.map((skill: any, i: number) => (
                        <Badge
                          key={i}
                          variant="secondary"
                          className="rounded-full px-3 py-1 text-xs font-medium transition-colors hover:bg-secondary/80"
                        >
                          {typeof skill === 'string' ? skill : skill.skill_name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* School Info */}
          <Card className="overflow-hidden border-border/40 bg-card/80 backdrop-blur-sm p-0">
            <div className="border-b border-border/40 bg-muted/30 px-6 py-4">
              <h2 className="flex items-center gap-3 text-lg font-semibold">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10">
                  <Building className="h-4.5 w-4.5 text-primary" />
                </div>
                School Details
              </h2>
            </div>
            <CardContent className="p-6">
              <div className="space-y-5">
                <div className="flex items-center gap-4">
                  <Avatar className="h-14 w-14 rounded-xl border-2 border-border/60 shadow-sm">
                    {school?.logo_url && (
                      <AvatarImage
                        src={school.logo_url || '/placeholder.svg'}
                        alt={school.name ?? ''}
                        className="object-cover"
                      />
                    )}
                    <AvatarFallback className="rounded-xl bg-muted text-sm font-semibold">
                      {school?.name?.slice(0, 2).toUpperCase() ?? 'SC'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold">{school?.name}</p>
                    {school?.slogan && (
                      <p className="truncate text-xs text-muted-foreground">
                        {school.slogan}
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-3 border-t border-border/40 pt-5">
                  {school?.email && (
                    <a
                      href={`mailto:${school.email}`}
                      className="group flex items-center gap-3 rounded-lg p-2 text-sm text-muted-foreground transition-all hover:bg-muted/50 hover:text-foreground"
                    >
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted/80 transition-colors group-hover:bg-primary/10">
                        <Mail className="h-4 w-4 transition-colors group-hover:text-primary" />
                      </div>
                      <span className="truncate">{school.email}</span>
                    </a>
                  )}
                  {school?.website && (
                    <a
                      href={`https://${school.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center gap-3 rounded-lg p-2 text-sm text-muted-foreground transition-all hover:bg-muted/50 hover:text-foreground"
                    >
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted/80 transition-colors group-hover:bg-primary/10">
                        <Globe className="h-4 w-4 transition-colors group-hover:text-primary" />
                      </div>
                      <span className="flex-1 truncate">{school.website}</span>
                      <ExternalLink className="h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-100" />
                    </a>
                  )}
                  {school?.address && (
                    <div className="flex items-start gap-3 rounded-lg p-2 text-sm text-muted-foreground">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted/80">
                        <Building className="h-4 w-4" />
                      </div>
                      <span className="pt-1.5">{school.address}</span>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Mentor Info */}
          {
            !!(!isCompany && !isExternal) && (
              <Card className="overflow-hidden border-border/40 bg-card/80 backdrop-blur-sm p-0">
                <div className="border-b border-border/40 bg-muted/30 px-6 py-4">
                  <h2 className="flex items-center gap-3 text-lg font-semibold">
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10">
                      <User className="h-4.5 w-4.5 text-primary" />
                    </div>
                    Project Mentor
                  </h2>
                </div>
                <CardContent className="p-6">
                  {project.project_mentor ? (
                    <div className="flex items-center gap-4">
                      <Avatar className="h-12 w-12 rounded-xl border-2 border-border/60 shadow-sm">
                        <AvatarFallback className="rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 text-sm font-semibold text-primary">
                          {mentorInitials}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium">
                          {project.project_mentor}
                        </p>
                        <p className="text-xs text-muted-foreground">Mentor</p>
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-xl border border-dashed border-border/60 bg-muted/20 p-4 text-center">
                      <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-muted/60">
                        <User className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <p className="text-sm italic text-muted-foreground">
                        No mentor assigned yet.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )
          }

          {/* Quick Info */}
          <Card className="overflow-hidden border-border/40 bg-card/80 backdrop-blur-sm p-0">
            <div className="border-b border-border/40 bg-muted/30 px-6 py-4">
              <h2 className="text-lg font-semibold">Quick Info</h2>
            </div>
            <CardContent className="p-6">
              <div className="space-y-4">
                {/* <div className="flex items-center justify-between rounded-lg bg-muted/30 p-3 transition-colors hover:bg-muted/50">
                  <span className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Hash className="h-4 w-4" />
                    Project ID
                  </span>
                  <span className="rounded-md bg-background px-2 py-0.5 font-mono text-sm font-semibold shadow-sm">
                    #{project.id}
                  </span>
                </div> */}
                <div className="flex items-center justify-between rounded-lg bg-muted/30 p-3 transition-colors hover:bg-muted/50">
                  <span className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    Duration
                  </span>
                  <span className="text-sm font-semibold">
                    {getDurationLabel(
                      project.project_duration,
                      project.project_duration_type
                    )}
                  </span>
                </div>
                <div className="flex items-center justify-between rounded-lg bg-muted/30 p-3 transition-colors hover:bg-muted/50">
                  <span className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="h-4 w-4" />
                    Published
                  </span>
                  <Badge
                    variant={project.published ? 'default' : 'secondary'}
                    className={`rounded-full text-xs ${project.published ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : ''}`}
                  >
                    {project.published ? 'Yes' : 'No'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between rounded-lg bg-muted/30 p-3 transition-colors hover:bg-muted/50">
                  <span className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    Created
                  </span>
                  <span className="text-sm font-semibold">
                    {formatDate(project.created_on, DATE_FORMAT)}
                  </span>
                </div>
                <div className="flex items-center justify-between rounded-lg bg-muted/30 p-3 transition-colors hover:bg-muted/50">
                  <span className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    Last Updated
                  </span>
                  <span className="text-sm font-semibold">
                    {formatDate(project.updated_on, DATE_FORMAT)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
